/**
 * 语音层结构化错误（T10）。
 *
 * 适配器（T10a/T10b）抛出的错误是 `TtsError` / `SttError` / `AbortError`，
 * 录音失败是普通 `Error`。本模块统一把它们折叠成 `VoiceIssue`，让 UI 只处理
 * 一种形状，避免任何异常直接冒泡到渲染树。
 */

import type { VoiceIssue, VoiceIssueSource } from './types';

/** 无适配器时的「未配置」错误。 */
export function createConfigIssue(source: VoiceIssueSource): VoiceIssue {
  return {
    source,
    code: 'config',
    message: configMessage(source),
  };
}

function configMessage(source: VoiceIssueSource): string {
  switch (source) {
    case 'tts':
      return '尚未配置 TTS 模型，请到设置页添加后再试';
    case 'transcribe':
      return '尚未配置语音转写模型，请到设置页添加后再试';
    case 'recorder':
      return '当前环境不支持录音（缺少麦克风权限或 mediaDevices）';
  }
}

/** 是否取消错误（`AbortError`）。 */
export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/** 读取错误对象上的规范化错误码（core 适配器的 `TtsError.code` / `SttError.code`）。 */
function readCode(error: unknown): string | null {
  if (error !== null && typeof error === 'object' && 'code' in error) {
    const code: unknown = (error as { code?: unknown }).code;
    if (typeof code === 'string' && code !== '') return code;
  }
  if (isAbortError(error)) return 'aborted';
  return null;
}

function readMessage(error: unknown): string | null {
  if (error instanceof Error && error.message !== '') return error.message;
  if (typeof error === 'string' && error !== '') return error;
  return null;
}

/** 任意异常 → 结构化错误，绝不再抛出。 */
export function toVoiceIssue(error: unknown, source: VoiceIssueSource): VoiceIssue {
  const code = readCode(error) ?? 'unknown';
  return {
    source,
    code,
    message: readMessage(error) ?? `语音能力调用失败（${source}/${code}）`,
  };
}